import { ProductizedServiceTabs_productizedService$key } from '#app/__generated__/relay/ProductizedServiceTabs_productizedService.graphql.js';
import { Text } from '#app/components/Primitives/Text.js';
import { ServicePageReviews } from '#app/components/ServicePageReviews/ServicePageReviews.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { useSafeUserReviews } from '#app/hooks/useSafeUserReviews.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { removeSearchParameter } from '#app/utilities/removeSearchParameter.js';
import { ProductizedServiceDeliverables } from '../ProductizedServiceDeliverables/ProductizedServiceDeliverables.js';
import { ProductizedServiceDescription } from '../ProductizedServiceDescription/ProductizedServiceDescription.js';
import { ProductizedServiceExampleProjects } from '../ProductizedServiceExampleProjects/ProductizedServiceExampleProjects.js';
import { useMount } from '@contra/react-hooks/useMount';
import { graphql, useFragment } from '@contra/relay';
import { useEffect, useRef, useState } from 'react';
import { css, styled } from 'styled-components';

const Container = styled.div`
  padding-top: 12px;
  width: 100%;
`;

const Gap = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 40px;
`;

const TabContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-flow: row nowrap;
    gap: 40px;
    align-items: center;
    max-width: 100vw;
    overflow-x: auto;
    padding-bottom: 16px;
    border-bottom: 1px solid ${theme.colorsV2.gray30};
    margin-bottom: 40px;
  `}
`;

const Tab = styled.button<{ $isActive: boolean }>`
  ${({ $isActive, theme }) => css`
    transition: color 175ms ease;
    white-space: nowrap;
    color: ${theme.colorsV2[$isActive ? 'black' : 'gray50']};
  `}
`;

type ProductizedServiceTabsProps = {
  readonly productizedServiceRef: ProductizedServiceTabs_productizedService$key;
};

type Tab = 'about' | 'deliverables' | 'recommendations';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'deliverables', label: "What's included" },
  { id: 'about', label: 'About this service' },
  { id: 'recommendations', label: 'Recommendations' },
];

const isValidTab = (tab: string): tab is Tab => {
  return tab === 'deliverables' || tab === 'about' || tab === 'recommendations';
};

const resolveDefaultTab = ({
  showDeliverables,
  showRecommendations,
  tab,
}: {
  showDeliverables: boolean;
  showRecommendations: boolean;
  tab?: string;
}): Tab => {
  const fallbackTab: Tab = showDeliverables ? 'deliverables' : 'about';

  if (!tab || !isValidTab(tab)) {
    return fallbackTab;
  }

  if (tab === 'recommendations' && !showRecommendations) {
    return fallbackTab;
  }

  if (tab === 'deliverables' && !showDeliverables) {
    return fallbackTab;
  }

  return tab ?? fallbackTab;
};

export const ProductizedServiceTabs = ({
  productizedServiceRef,
}: ProductizedServiceTabsProps) => {
  const isMobile = useIsMobile();
  const service = useFragment<ProductizedServiceTabs_productizedService$key>(
    graphql`
      fragment ProductizedServiceTabs_productizedService on ProductizedService {
        deliverables {
          count
        }
        userProfile {
          ...useSafeUserReviews_userProfile
        }
        ...ProductizedServiceDescription_productizedService
        ...ProductizedServiceExampleProjects_productizedService
        ...ProductizedServiceDeliverables_productizedService
        ...ServicePageReviews_productizedService
      }
    `,
    productizedServiceRef,
  );

  const { reviews } = useSafeUserReviews({
    userProfileRef: service.userProfile,
  });
  const showRecommendations = reviews.length > 0;
  const showDeliverables = service.deliverables.count > 0;
  const [urlParameters] = useURLParameters('/s/@slug');
  const onMountSearchParameterHandledRef = useRef(false);
  const { replace } = useNavigation();
  const location = useLocation();
  const tab = urlParameters.tab;
  const [currentTab, setCurrentTab] = useState<Tab>(
    resolveDefaultTab({ showDeliverables, showRecommendations, tab }),
  );

  const onClickTab = (newTab: Tab) => () => {
    setCurrentTab(newTab);
  };

  const isActiveTab = (queryTab: Tab) => queryTab === currentTab;

  useMount(() => {
    // cleanup dirty params yuck
    replace(removeSearchParameter(location.path, 'tab'));
    onMountSearchParameterHandledRef.current = true;
  });

  useEffect(() => {
    // first let useMount do its thing
    if (!onMountSearchParameterHandledRef.current) {
      return;
    }

    // currently doing this only for recommendations as that's the only tab
    // that's being selected from outside the scope of this component
    if (tab === 'recommendations') {
      setCurrentTab(tab);
      // based on the comment in the useMount above, it seems that params are yucky and need to be cleaned up
      replace(removeSearchParameter(location.path, 'tab'));
    }
  }, [location.path, replace, tab]);

  if (isMobile) {
    return (
      <>
        <ProductizedServiceDescription productizedServiceRef={service} />
        <ProductizedServiceDeliverables productizedServiceRef={service} />
        <ProductizedServiceExampleProjects productizedServiceRef={service} />
        <ServicePageReviews productizedServiceRef={service} />
      </>
    );
  }

  return (
    <Container>
      <TabContainer>
        {TABS.filter(({ id }) => {
          if (id === 'recommendations') {
            return showRecommendations;
          }

          if (id === 'deliverables') {
            return showDeliverables;
          }

          return true;
        }).map(({ id, label }) => (
          <Tab
            $isActive={isActiveTab(id)}
            key={`service-tab-${id}`}
            onClick={onClickTab(id)}
          >
            <Text textStyle="subtitleRegular">{label}</Text>
          </Tab>
        ))}
      </TabContainer>
      {currentTab === 'deliverables' && (
        <ProductizedServiceDeliverables productizedServiceRef={service} />
      )}
      {currentTab === 'about' && (
        <Gap>
          <ProductizedServiceDescription productizedServiceRef={service} />
          <ProductizedServiceExampleProjects productizedServiceRef={service} />
        </Gap>
      )}
      {currentTab === 'recommendations' && (
        <ServicePageReviews
          hideHeader
          productizedServiceRef={service}
        />
      )}
    </Container>
  );
};
